import {
  createContext,
  ReactElement,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { Card } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Course,
  CourseFormData,
  RetakeAllowedCodes,
  CourseFormMode,
} from '../types';

import Loading from '../../../../components/info/Loading';
import DisplayError from '../../../../components/info/DisplayError';

import {
  getSchoolListRequest,
  requestCourseTypes,
  requestGetCourse,
} from '../api';
import { baseToSelectOption } from '../utils';
import { TranslationsKeys } from '../../../../locales/translations';

export interface CourseFormContextInterface {
  courseId?: number;
  course?: Course;
  isLoading: boolean;
  isDisabledToUpdate: boolean;
  mode: CourseFormMode;
  formData: CourseFormData;
}

export const CourseFormContext = createContext<CourseFormContextInterface>({
  isLoading: false,
  isDisabledToUpdate: false,
  mode: CourseFormMode.CREATE,
  formData: {},
});

type CourseProviderProps = {
  courseId?: string;
  children: ReactElement;
};

const CourseProvider = ({ courseId, children }: CourseProviderProps) => {
  const location = useLocation();
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const [course, setCourse] = useState<Course>();
  const [formData, setFormData] = useState<CourseFormData>({});

  const [isLoadingCourse, setIsLoadingCourse] = useState<boolean>(false);
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
  const [errorLoading, setErrorLoading] = useState<string>();

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isMountedForm, setIsMountedForm] = useState<boolean>(false);

  const isLoading = useMemo(() => isLoadingCourse || isLoadingForm, [
    isLoadingForm,
    isLoadingCourse,
  ]);

  const mode = useMemo(() => {
    if (courseId && location.pathname.includes('watch')) {
      return CourseFormMode.WATCH;
    } else if (courseId && location.pathname.includes('update')) {
      return CourseFormMode.UPDATE;
    } else {
      return CourseFormMode.CREATE;
    }
  }, [courseId, location]);

  const isDisabledToUpdate = useMemo(
    () =>
      !!(
        course &&
        (course?.sections?.length || course?.semesterCourses?.length)
      ),
    [course],
  );

  const fetchCourse = useCallback(
    async (id: number) => {
      if (isLoadingCourse) {
        return;
      }
      setIsLoadingCourse(true);
      try {
        const { data: response, error } = await requestGetCourse(id);
        if (error || !response?.data) {
          throw new Error('ERROR');
        }
        setCourse(response?.data);
      } catch (error) {
        setCourse(undefined);
        setErrorLoading('ERROR');
      }
      setIsLoadingCourse(false);
    },
    [isLoadingCourse],
  );

  const fetchFormData = useCallback(async () => {
    if (isLoadingForm) {
      return;
    }
    setIsLoadingForm(true);
    try {
      const [
        { data: formDataResponse, error: errorFormData },
        { data: courseTypesResponse, error: errorCoursesTypes },
      ] = await Promise.all([getSchoolListRequest(), requestCourseTypes()]);
      if (errorFormData || errorCoursesTypes) {
        throw new Error('ERROR');
      }
      setFormData({
        schools: baseToSelectOption(formDataResponse?.schools),
        academicLevels: baseToSelectOption(formDataResponse?.academicLevels),
        activities: baseToSelectOption(formDataResponse?.activities),
        courseTypes: baseToSelectOption(courseTypesResponse?.data),
        modalities: baseToSelectOption(formDataResponse?.modalities),
        studyPlanVersions: baseToSelectOption(
          formDataResponse?.studyPlanVersions,
        ),
        replaceCourseGradeOptions: [
          {
            label: t(
              `replaceCourseGradeAllowed_${RetakeAllowedCodes.Disabled}`,
            ),
            value: RetakeAllowedCodes.Disabled,
          },
          {
            label: t(`replaceCourseGradeAllowed_${RetakeAllowedCodes.Enabled}`),
            value: RetakeAllowedCodes.Enabled,
          },
        ],
        coursesFeatures: formDataResponse?.coursesFeatures,
      });
    } catch (error) {
      setFormData({});
      setErrorLoading('ERROR');
    }
    setIsLoadingForm(false);
  }, [isLoadingForm, t]);

  const refetch = useCallback(() => {
    setErrorLoading(undefined);
    fetchFormData();
    if (courseId) {
      fetchCourse(+courseId);
    }
  }, [courseId, fetchFormData, fetchCourse]);

  useEffect(() => {
    if (!isMounted && courseId) {
      fetchCourse(+courseId);
      setIsMounted(true);
    }
  }, [isMounted, courseId, fetchCourse]);

  useEffect(() => {
    if (!isMountedForm) {
      fetchFormData();
      setIsMountedForm(true);
    }
  }, [isMountedForm, fetchFormData]);

  if (isLoading && !errorLoading) {
    return (
      <Card className="p-4 mt-2 mx-3">
        <div className="mx-3">
          <Loading insideCard textBody={''} />
        </div>
      </Card>
    );
  }

  if (errorLoading) {
    return (
      <div className="mx-3">
        <DisplayError
          insideCard
          textBody={errorLoading}
          retryAction={refetch}
          loadingAction={isLoading}
        />
      </div>
    );
  }

  return (
    <CourseFormContext.Provider
      value={{
        courseId: courseId ? +courseId : undefined,
        isDisabledToUpdate,
        isLoading,
        course,
        formData,
        mode,
      }}
    >
      <Card className="p-4 mt-2 mx-3">{children}</Card>
    </CourseFormContext.Provider>
  );
};

export default CourseProvider;
